<template>
    <div class="profile-header">
        <v-row>
            <v-col cols="4" md="3" xl="3">
                <v-avatar
                    :size="$vuetify.breakpoint.smAndUp ? 125 : 80"
                    color="#F4F5F6"
                >
                    <v-img :src="user.image_url">
                        <template v-slot:placeholder>
                            <v-icon color="grey lighten-1" size="40">mdi-account-outline</v-icon>
                        </template>
                    </v-img>
                </v-avatar>
            </v-col>
            <v-col cols="8" md="9" xl="9" class="pb-4">
                <v-row>
                    <v-col class="pb-0">
                        <h3 class="user-name">{{ user.name }}</h3>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col class="pb-0 pt-2">
                        <p class="user-university">{{ universityName }}</p>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="d-flex need-roommate pb-1 flex-column flex-sm-row">
                        <h5>Need a roommate?</h5>
                        <v-switch
                            inset
                            v-model="user.needs_roommate"
                            class="pa-0 ma-0 pl-0 pl-sm-4 py-4 py-sm-0"
                            hide-details
                        ></v-switch>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="pt-0 roommate-info">
                        Turning this off will set your profile to private and nobody will see it
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

        <!-- Profile Completion Progress Bars -->
        <v-row v-if="profileCompletion" class="mt-4">
            <v-col cols="12">
                <div class="completion-section">
                    <div class="completion-header d-flex justify-space-between align-center mb-3">
                        <span class="completion-title">Profile Completion</span>
                        <span class="overall-percent">{{ overallCompletion }}%</span>
                    </div>
                    <v-progress-linear
                        :value="overallCompletion"
                        height="10"
                        rounded
                        color="#2D76E3"
                        background-color="#E8F0FE"
                        class="mb-4"
                    />
                    <v-row>
                        <v-col cols="6" sm="3" v-for="section in completionSections" :key="section.key">
                            <v-tooltip
                                bottom
                                :disabled="profileCompletion[section.key] === 100"
                                max-width="250"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <div
                                        class="section-progress"
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="onSectionClick(section.key)"
                                    >
                                        <div class="d-flex justify-space-between align-center mb-1">
                                            <span class="section-label">{{ section.label }}</span>
                                            <span class="section-percent">{{ profileCompletion[section.key] }}%</span>
                                        </div>
                                        <v-progress-linear
                                            :value="profileCompletion[section.key]"
                                            height="6"
                                            rounded
                                            :color="getProgressColor(profileCompletion[section.key])"
                                            background-color="#E8F0FE"
                                        />
                                    </div>
                                </template>
                                <div class="tooltip-content">
                                    <strong>Missing:</strong>
                                    <ul class="missing-list">
                                        <li v-for="item in getMissingFields(section.key)" :key="item">{{ item }}</li>
                                    </ul>
                                </div>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    name: 'ProfileHeader',
    props: {
        user: {
            type: Object,
            required: true
        },
        universityName: {
            type: String,
            default: ''
        },
        profileCompletion: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            completionSections: [
                { key: 'registration', label: 'Registration' },
                { key: 'rental_profile', label: 'Rental' },
                { key: 'roommate_profile', label: 'Roommate' },
                { key: 'campaign', label: 'Campaign' }
            ]
        }
    },
    computed: {
        overallCompletion() {
            if (!this.profileCompletion) return 0
            const total = this.completionSections.reduce((sum, section) => {
                return sum + (this.profileCompletion[section.key] || 0)
            }, 0)
            return Math.round(total / this.completionSections.length)
        }
    },
    methods: {
        getProgressColor(value) {
            if (value >= 100) return '#4CAF50'
            if (value >= 50) return '#2D76E3'
            return '#FF9800'
        },
        onSectionClick(sectionKey) {
            this.$emit('section-click', sectionKey)
        },
        getMissingFields(sectionKey) {
            const missing = []

            if (sectionKey === 'registration') {
                // Name, email, phone are in Account Settings tab
                if (!this.user.name) missing.push('Name (Account Settings tab)')
                if (!this.user.email) missing.push('Email (Account Settings tab)')
                if (!this.user.phone_number) missing.push('Phone (Account Settings tab)')
                // Gender, DOB are in Registration section below
                if (!this.user.gender) missing.push('Gender')
                if (!this.user.date_of_birth) missing.push('Date of birth')
            }

            if (sectionKey === 'rental_profile') {
                if (!this.user.campus_id) missing.push('School Name')
                if (!this.user.university_start_date) missing.push('Estimated Start Date')
                if (this.user.preferred_bedrooms === null || this.user.preferred_bedrooms === undefined) missing.push('Bedroom preference')
                if (this.user.preferred_bathrooms === null || this.user.preferred_bathrooms === undefined) missing.push('Bathroom preference')
                if (this.user.preferred_max_price === null || this.user.preferred_max_price === undefined) missing.push('Max Price')
            }

            if (sectionKey === 'roommate_profile') {
                if (!this.user.needs_roommate) {
                    return ['Roommate search is off - enable above to complete']
                }
                if (!this.user.image_url && !this.user.image_path) missing.push('Profile photo')
                if (!this.user.hobbies) missing.push('Hobbies')
                const hasSocial = this.user.instagram_username || this.user.tiktok_username ||
                    this.user.snapchat_username || this.user.facebook_username
                if (!hasSocial) missing.push('Social media (at least 1)')
                if (!this.user.ideal_roommate_gender) missing.push('Roommate gender preference')
                if (!this.user.ideal_roommate_age_young || !this.user.ideal_roommate_age_old) missing.push('Roommate age range')
            }

            if (sectionKey === 'campaign') {
                if (!this.user.stripe) {
                    missing.push('Connect Stripe (Crowdfunding tab)')
                } else if (!this.user.stripe.onboarding_completed) {
                    missing.push('Complete Stripe onboarding (Crowdfunding tab)')
                } else if (!this.user.campaign) {
                    missing.push('Start a campaign (Crowdfunding tab)')
                }
            }

            return missing.length > 0 ? missing : ['All complete!']
        }
    }
}
</script>

<style scoped>
.profile-header .user-name {
    font-size: 26px !important;
    font-weight: 700;
}

.profile-header .user-university {
    font-size: 16px !important;
    font-weight: 400;
}

.profile-header .need-roommate h5 {
    font-size: 16px !important;
    font-weight: 700;
}

.profile-header .roommate-info {
    font-size: 12px !important;
    color: #000;
    font-style: italic;
}

.completion-section {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 16px;
}

.completion-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
}

.overall-percent {
    font-size: 16px;
    font-weight: 700;
    color: #2D76E3;
}

.section-progress {
    min-width: 0;
    cursor: pointer;
    padding: 8px;
    margin: -8px;
    border-radius: 8px;
    transition: background-color 0.2s ease;
}

.section-progress:hover {
    background-color: rgba(45, 118, 227, 0.08);
}

.section-label {
    font-size: 11px;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.section-percent {
    font-size: 11px;
    font-weight: 600;
    color: #333;
}

.tooltip-content {
    padding: 4px 0;
}

.tooltip-content .missing-list {
    margin: 4px 0 0 0;
    padding-left: 16px;
}

.tooltip-content .missing-list li {
    margin: 2px 0;
}
</style>
